import React from "react";
import ThemePageShell, { ThemeField, isValidHex } from "./ThemePageShell";
import { ThemePayload } from "../../context/ThemeContext";

const fields: ThemeField[] = [
  {
    name: "themeName",
    label: "Theme name",
    type: "text",
    placeholder: "Corporate Edge",
    required: true,
    helper: "Internal name for the corporate premium theme.",
  },
  {
    name: "primaryAccent",
    label: "Primary accent color",
    type: "color",
    placeholder: "#0EA5E9",
    required: true,
    helper: "Pick a strong accent for CTA buttons and headers.",
  },
  {
    name: "sidebarColor",
    label: "Sidebar background color",
    type: "color",
    placeholder: "#1E293B",
    required: true,
    helper: "Background color for the left navigation sidebar.",
  },
  {
    name: "sidebarTextColor",
    label: "Sidebar text color",
    type: "color",
    placeholder: "#CBD5E1",
    required: true,
    helper: "Text and icon color for sidebar navigation items.",
  },
  {
    name: "topbarColor",
    label: "Top bar background color",
    type: "color",
    placeholder: "#0F172A",
    required: true,
    helper: "Background color for the top navigation bar.",
  },
  {
    name: "topbarTextColor",
    label: "Top bar text color",
    type: "color",
    placeholder: "#F1F5F9",
    required: true,
    helper: "Text color for the top navigation bar.",
  },
  {
    name: "accessLevel",
    label: "Access level",
    type: "select",
    placeholder: "Select access",
    required: true,
    options: ["Admin only", "Managers + Admins", "Everyone"],
    helper: "Control who can activate or edit this theme.",
  },
  {
    name: "widgetSet",
    label: "Dashboard widget set",
    type: "select",
    placeholder: "Choose widget set",
    required: true,
    options: ["Executive summary", "Operations stream", "Security dashboard"],
    helper: "Choose the curated widget set for the theme.",
  },
  {
    name: "supportEmail",
    label: "Support email",
    type: "text",
    placeholder: "support@yourdomain.com",
    required: true,
    helper: "Email address displayed in theme support sections.",
  },
  {
    name: "enableEncryption",
    label: "Encrypt backend payloads",
    type: "select",
    placeholder: "Select option",
    required: true,
    options: ["Enabled", "Disabled"],
    helper:
      "Indicates whether the theme uses encrypted configuration payloads.",
  },
  {
    name: "auditTracking",
    label: "Audit tracking",
    type: "textarea",
    placeholder: "e.g. themeInstall, changes, accessGrant",
    required: true,
    helper: "Enter the backend audit event names this theme should emit.",
  },
];

const featureList = [
  "Enterprise-ready access and audit controls",
  "Secure support metadata and payload encryption flags",
  "Theme options built for role-based dashboards",
  "Backend-ready fields for secure API integration",
];

const palette: ThemePayload = {
  sidebarBackground: "#1E293B",
  sidebarTextColor: "#CBD5E1",
  topbarBackground: "#0F172A",
  topbarTextColor: "#F1F5F9",
  primaryAccent: "#0EA5E9",
  surface: "#F0F9FF",
};

const ThemeCorporateEdge: React.FC = () => (
  <ThemePageShell
    title="Corporate Edge Theme"
    subtitle="A business-focused premium theme with enterprise security controls, audit-ready fields, and clear backend contract structure."
    description="Complete the corporate theme form to define the theme payload and enforce stricter controls on what can be configured through the UI."
    featureList={featureList}
    fields={fields}
    cta="Save Corporate Theme"
    pathLabel="Premium Themes"
    themePayload={palette}
    baseSlug="corporate-edge"
    liveFieldNames={["primaryAccent", "sidebarColor", "sidebarTextColor", "topbarColor", "topbarTextColor"]}
    getLiveOverrides={(values) => {
      const overrides: Partial<ThemePayload> = {};
      if (isValidHex(values.primaryAccent)) overrides.primaryAccent = values.primaryAccent;
      if (isValidHex(values.sidebarColor)) overrides.sidebarBackground = values.sidebarColor;
      if (isValidHex(values.sidebarTextColor)) overrides.sidebarTextColor = values.sidebarTextColor;
      if (isValidHex(values.topbarColor)) overrides.topbarBackground = values.topbarColor;
      if (isValidHex(values.topbarTextColor)) overrides.topbarTextColor = values.topbarTextColor;
      return overrides;
    }}
  />
);

export default ThemeCorporateEdge;
